import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, FolderIcon, ChartBarIcon, StarIcon } from '@heroicons/react/24/outline';
import { FolderIcon as FolderSolidIcon } from '@heroicons/react/24/solid';
import { authorizedFetch } from '../utils/api';

const Collections = () => {
    const [collections, setCollections] = useState([]);
    const [currentCollection, setCurrentCollection] = useState(null);
    const [looks, setLooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collectionsLoading, setCollectionsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLook, setSelectedLook] = useState(null);
    const [sortBy, setSortBy] = useState('index');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filterBy, setFilterBy] = useState('all');
    const [collectionsPage, setCollectionsPage] = useState(1);
    const [totalCollections, setTotalCollections] = useState(0);
    const [collectionsSortBy, setCollectionsSortBy] = useState('created_at');
    const [collectionsSortOrder, setCollectionsSortOrder] = useState('desc');
    const [availableBrands, setAvailableBrands] = useState([]);
    const [availableDesigners, setAvailableDesigners] = useState([]);
    const [collectionsBrandFilter, setCollectionsBrandFilter] = useState('');
    const [collectionsDesignerFilter, setCollectionsDesignerFilter] = useState('');
    const [collectionSearch, setCollectionSearch] = useState('');
    const [isCollectionReviewVisible, setIsCollectionReviewVisible] = useState(true);
    const [overallStats, setOverallStats] = useState(null);
    const [renderTime, setRenderTime] = useState(null);
    const [showRenderTime, setShowRenderTime] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [modalManualScore, setModalManualScore] = useState('');
    const [modalManualError, setModalManualError] = useState('');
    const renderStartRef = React.useRef(null);

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    // Fetch all collections on component mount
    useEffect(() => {
        fetchAllCollections();
        fetchOverallStats();
    }, [collectionsPage, collectionsSortBy, collectionsSortOrder, collectionsBrandFilter, collectionsDesignerFilter]);

    useEffect(() => {
        fetchDesignerOptions();
    }, []);

    // Fetch latest collection when collections are loaded
    useEffect(() => {
        if (collections.length > 0 && !currentCollection) {
            fetchLatestCollection();
        }
    }, [collections]);

    useEffect(() => {
        if (!selectedLook) {
            setModalManualScore('');
            setModalManualError('');
            return;
        }
        setModalManualScore(
            selectedLook.buyer_score && selectedLook.buyer_score > 0
                ? String(selectedLook.buyer_score)
                : ''
        );
        setModalManualError('');
    }, [selectedLook?.id]);

    const fetchOverallStats = async () => {
        try {
            const response = await authorizedFetch(`${backendUrl}/collections/statistics`);
            if (response.ok) {
                const data = await response.json();
                setOverallStats(data);
            }
        } catch (err) {
            console.error('Error fetching overall stats:', err);
        }
    };

    const fetchDesignerOptions = async () => {
        try {
            const response = await authorizedFetch(`${backendUrl}/fashion-looks/filters`);
            if (response.ok) {
                const data = await response.json();
                const brands = Array.isArray(data?.brands) ? data.brands.filter(Boolean) : [];
                const designers = Array.isArray(data?.designers) ? data.designers.filter(Boolean) : [];
                setAvailableBrands((prev) => Array.from(new Set([...prev, ...brands])).sort());
                setAvailableDesigners((prev) => Array.from(new Set([...prev, ...designers])).sort());
            }
        } catch (err) {
            console.error('Error fetching designer options:', err);
        }
    };

    const fetchAllCollections = async () => {
        renderStartRef.current = performance.now();
        setCollectionsLoading(true);
        setError(null);

        try {
            // Try to fetch collections with stats first, fallback to basic collections
            const brandParam = collectionsBrandFilter ? `&brand=${encodeURIComponent(collectionsBrandFilter)}` : '';
            const designerParam = collectionsDesignerFilter ? `&designer=${encodeURIComponent(collectionsDesignerFilter)}` : '';
            let response = await authorizedFetch(`${backendUrl}/collections/with-stats?limit=50${brandParam}${designerParam}`);

            if (response.ok) {
                const data = await response.json();
                const list = Array.isArray(data) ? data : Array.isArray(data?.collections) ? data.collections : [];
                setCollections(list);
                setTotalCollections(Array.isArray(data) ? data.length : (data?.total || list.length));
                const brandsFromCollections = list.map((c) => c?.brand).filter(Boolean);
                const designersFromCollections = list.map((c) => c?.designer).filter(Boolean);
                setAvailableBrands((prev) => Array.from(new Set([...prev, ...brandsFromCollections])).sort());
                setAvailableDesigners((prev) => Array.from(new Set([...prev, ...designersFromCollections])).sort());
            } else {
                // Fallback to basic collections endpoint
                response = await authorizedFetch(`${backendUrl}/collections/?page=${collectionsPage}&limit=20&sort_by=${collectionsSortBy}&sort_order=${collectionsSortOrder}${brandParam}${designerParam}`);
                if (response.ok) {
                    const data = await response.json();
                    const list = Array.isArray(data?.collections) ? data.collections : [];
                    setCollections(list);
                    setTotalCollections(data?.total || list.length);
                    const brandsFromCollections = list.map((c) => c?.brand).filter(Boolean);
                    const designersFromCollections = list.map((c) => c?.designer).filter(Boolean);
                    setAvailableBrands((prev) => Array.from(new Set([...prev, ...brandsFromCollections])).sort());
                    setAvailableDesigners((prev) => Array.from(new Set([...prev, ...designersFromCollections])).sort());
                }
            }

            // Performance tracking
            const end = performance.now();
            const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
            setRenderTime(seconds);
            setShowRenderTime(true);
            setTimeout(() => setShowRenderTime(false), 5000);

        } catch (err) {
            console.error('Error fetching collections:', err);
            setError('Failed to load collections');
        } finally {
            setCollectionsLoading(false);
        }
    };

    const filteredDesignerOptions = useMemo(() => {
        if (!collectionsBrandFilter) return availableDesigners;
        const filtered = collections
            .filter((c) => (c?.brand || '').toLowerCase() === collectionsBrandFilter.toLowerCase())
            .map((c) => c?.designer)
            .filter(Boolean);
        const unique = Array.from(new Set(filtered)).sort();
        return unique.length > 0 ? unique : availableDesigners;
    }, [collections, collectionsBrandFilter, availableDesigners]);

    const displayedCollections = useMemo(() => {
        const q = collectionSearch.trim().toLowerCase();
        const list = Array.isArray(collections) ? collections : [];
        if (!q) return list;
        return list.filter((c) => {
            const brand = (c?.brand || '').toLowerCase();
            const designer = (c?.designer || '').toLowerCase();
            const season = (c?.season || '').toLowerCase();
            return brand.includes(q) || designer.includes(q) || season.includes(q);
        });
    }, [collections, collectionSearch]);

    const resetCollectionControls = () => {
        setCollectionsPage(1);
        setCollectionsSortBy('created_at');
        setCollectionsSortOrder('desc');
        setCollectionsBrandFilter('');
        setCollectionsDesignerFilter('');
        setCollectionSearch('');
    };

    const fetchLatestCollection = async () => {
        setLoading(true);
        try {
            const response = await authorizedFetch(`${backendUrl}/fashion-looks/latest-collection`);
            if (response.ok) {
                const data = await response.json();
                setCurrentCollection(data.metadata);
                setLooks(data.looks);
            }
        } catch (err) {
            console.error('Error fetching latest collection:', err);
            setError('Failed to load collection');
        } finally {
            setLoading(false);
        }
    };

    const fetchCollectionById = async (collectionId) => {
        setLoading(true);
        try {
            const response = await authorizedFetch(`${backendUrl}/fashion-looks/collection/${collectionId}`);
            if (response.ok) {
                const data = await response.json();
                setCurrentCollection(data.metadata);
                setLooks(data.looks);
            }
        } catch (err) {
            console.error('Error fetching collection:', err);
            setError('Failed to load collection');
        } finally {
            setLoading(false);
        }
    };

    // Save scroll position before updating
    const saveScrollPosition = () => {
        sessionStorage.setItem('collectionScrollPosition', window.pageYOffset);
    };

    // Restore scroll position
    const restoreScrollPosition = () => {
        const savedPosition = sessionStorage.getItem('collectionScrollPosition');
        if (savedPosition) {
            window.scrollTo(0, parseInt(savedPosition));
        }
    };

    // Add effect to restore scroll position on component mount
    useEffect(() => {
        restoreScrollPosition();
        // Clean up
        return () => {
            sessionStorage.removeItem('collectionScrollPosition');
        };
    }, []);

    const updateBuyerScore = async (lookId, score) => {
        // Save current scroll position before making the API call
        saveScrollPosition();

        try {
            const response = await authorizedFetch(`${backendUrl}/fashion-looks/buyer-score`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    look_id: lookId,
                    buyer_score: score
                })
            });

            if (response.ok) {
                // Update the look in the local state
                setLooks(prevLooks =>
                    prevLooks.map(look =>
                        look.id === lookId ? { ...look, buyer_score: score } : look
                    )
                );

                // Update current collection stats
                if (currentCollection) {
                    const updatedLooks = looks.map(look =>
                        look.id === lookId ? { ...look, buyer_score: score } : look
                    );
                    const scoredLooks = updatedLooks.filter(l => l.buyer_score && l.buyer_score > 0);
                    const avgScore = scoredLooks.length > 0
                        ? Math.round((scoredLooks.reduce((sum, l) => sum + l.buyer_score, 0) / scoredLooks.length) * 10) / 10
                        : 0;
                    const highRated = updatedLooks.filter(l => l.buyer_score && l.buyer_score >= 8).length;
                    const unrated = updatedLooks.filter(l => !l.buyer_score || l.buyer_score === 0).length;

                    setCurrentCollection(prev => ({
                        ...prev,
                        avg_score: avgScore,
                        high_rated: highRated,
                        unrated: unrated
                    }));
                }

                // Restore scroll position after state updates
                setTimeout(restoreScrollPosition, 0);
            }
        } catch (err) {
            console.error('Error updating buyer score:', err);
        }
    };

    const getSortedAndFilteredLooks = () => {
        let filteredLooks = [...looks];

        // Apply filters
        switch (filterBy) {
            case 'rated':
                filteredLooks = filteredLooks.filter(look => look.buyer_score && look.buyer_score > 0);
                break;
            case 'unrated':
                filteredLooks = filteredLooks.filter(look => !look.buyer_score || look.buyer_score === 0);
                break;
            case 'high_rated':
                filteredLooks = filteredLooks.filter(look => look.buyer_score && look.buyer_score >= 8);
                break;
            default:
                break;
        }

        // Apply sorting
        filteredLooks.sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortBy === 'buyer_score' || sortBy === 'machine_score') {
                aVal = aVal || 0;
                bVal = bVal || 0;
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        return filteredLooks;
    };

    const detectReviewLanguage = (reviewText = '') => {
        return /[\u4e00-\u9fff]/.test(reviewText) ? '中文' : 'English';
    };

    const ScoreButton = ({ score, currentScore, onClick }) => (
        <button
            onClick={(e) => {
                e.preventDefault(); // Prevent default behavior
                onClick(score);
            }}
            className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${currentScore === score
                ? 'bg-blue-500 text-white shadow-md'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
        >
            {score}
        </button>
    );

    // Loading Skeleton Component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-500 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {[...Array(12)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                        <div className="aspect-[2/3] bg-gray-300 dark:bg-gray-600"></div>
                        <div className="p-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-500 rounded mb-1"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-500 rounded"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const LookCard = ({ look }) => {
        const [manualScore, setManualScore] = useState(look.buyer_score ? String(look.buyer_score) : '');
        const [manualError, setManualError] = useState('');

        const submitManualScore = () => {
            const parsed = parseFloat(manualScore);
            if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
                setManualError('Enter 0-10 (decimals allowed).');
                return;
            }
            setManualError('');
            updateBuyerScore(look.id, Math.round(parsed * 100) / 100);
        };

        return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative">
                <div className="w-full aspect-[2/3] bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                    <img
                        src={look.image_url}
                        alt={`Look ${look.index}`}
                        className="w-full h-full object-contain cursor-pointer"
                        onClick={() => setSelectedLook(look)}
                        onError={(e) => {
                            e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
                        }}
                    />
                </div>
                <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                    #{look.index}
                </div>
                {look.machine_score && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-sm">
                        AI: {look.machine_score}
                    </div>
                )}
            </div>
            <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        Buyer Score
                    </span>
                    <span className={`text-lg font-bold ${look.buyer_score >= 8 ? 'text-green-600' :
                        look.buyer_score >= 6 ? 'text-yellow-600' :
                            look.buyer_score > 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                        {look.buyer_score || 'Not rated'}
                    </span>
                </div>
                <div className="flex flex-wrap gap-1 justify-center">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                        <ScoreButton
                            key={score}
                            score={score}
                            currentScore={look.buyer_score}
                            onClick={(score) => updateBuyerScore(look.id, score)}
                        />
                    ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={manualScore}
                        onChange={(e) => setManualScore(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitManualScore();
                            }
                        }}
                        placeholder="e.g. 7.5"
                        className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                        type="button"
                        onClick={submitManualScore}
                        className="px-2 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600"
                    >
                        Save
                    </button>
                </div>
                {manualError && <div className="mt-1 text-xs text-red-500">{manualError}</div>}
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-300">
                    <span className="font-medium">AI Auxiliary Buying Decision:</span>{' '}
                    {look.ai_auxiliary_buying_decision && String(look.ai_auxiliary_buying_decision).trim()
                        ? look.ai_auxiliary_buying_decision
                        : 'N/A'}
                </div>
            </div>
        </div>
    );
    };

    // Sidebar Component
    const Sidebar = () => (
        <div className={`${sidebarOpen ? 'w-80' : 'w-16'} transition-all duration-300 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full`}>
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    {sidebarOpen && (
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                            <FolderSolidIcon className="h-5 w-5 mr-2 text-blue-600" />
                            Collections
                        </h2>
                    )}
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <ChevronLeftIcon className={`h-5 w-5 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} />
                    </button>
                </div>
            </div>

            {/* Overall Statistics */}
            {sidebarOpen && overallStats && (
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Overview</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <FolderIcon className="h-4 w-4 text-blue-500 mr-2" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Collections</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {overallStats.total_collections}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <ChartBarIcon className="h-4 w-4 text-green-500 mr-2" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Total Looks</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {overallStats.total_looks}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <StarIcon className="h-4 w-4 text-yellow-500 mr-2" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Progress</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {overallStats.rating_progress}%
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Collections List */}
            <div className="flex-1 overflow-y-auto">
                {sidebarOpen && (
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                All Collections ({totalCollections})
                            </h3>
                            <button
                                onClick={resetCollectionControls}
                                className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                Reset
                            </button>
                        </div>

                        <div className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20 space-y-3">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                Search Collections
                            </label>
                            <input
                                type="text"
                                value={collectionSearch}
                                onChange={(e) => setCollectionSearch(e.target.value)}
                                placeholder="Brand / Designer / Season"
                                className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Sort By
                                    </label>
                                    <select
                                        value={collectionsSortBy}
                                        onChange={(e) => {
                                            setCollectionsPage(1);
                                            setCollectionsSortBy(e.target.value);
                                        }}
                                        className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="created_at">Date</option>
                                        <option value="brand">Brand</option>
                                        <option value="designer">Designer</option>
                                        <option value="season">Season</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Order
                                    </label>
                                    <select
                                        value={collectionsSortOrder}
                                        onChange={(e) => {
                                            setCollectionsPage(1);
                                            setCollectionsSortOrder(e.target.value);
                                        }}
                                        className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="desc">Newest First</option>
                                        <option value="asc">Oldest First</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Brand
                                </label>
                                <select
                                    value={collectionsBrandFilter}
                                    onChange={(e) => {
                                        const brand = e.target.value;
                                        setCollectionsPage(1);
                                        setCollectionsBrandFilter(brand);
                                        if (!brand) {
                                            return;
                                        }
                                        if (collectionsDesignerFilter) {
                                            const stillValid = filteredDesignerOptions.some(
                                                (d) => d.toLowerCase() === collectionsDesignerFilter.toLowerCase()
                                            );
                                            if (!stillValid) setCollectionsDesignerFilter('');
                                        }
                                    }}
                                    className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">All Brands</option>
                                    {availableBrands.map((brand) => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Designer
                                </label>
                                <select
                                    value={collectionsDesignerFilter}
                                    onChange={(e) => {
                                        setCollectionsPage(1);
                                        setCollectionsDesignerFilter(e.target.value);
                                    }}
                                    className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">All Designers</option>
                                    {filteredDesignerOptions.map((designer) => (
                                        <option key={designer} value={designer}>
                                            {designer}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {(collectionsBrandFilter || collectionsDesignerFilter || collectionSearch.trim()) && (
                                <div className="flex flex-wrap gap-1 pt-1">
                                    {collectionsBrandFilter && (
                                        <span className="text-[10px] px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                            Brand: {collectionsBrandFilter}
                                        </span>
                                    )}
                                    {collectionsDesignerFilter && (
                                        <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                                            Designer: {collectionsDesignerFilter}
                                        </span>
                                    )}
                                    {collectionSearch.trim() && (
                                        <span className="text-[10px] px-2 py-1 rounded-full bg-violet-100 text-violet-700">
                                            Search: {collectionSearch.trim()}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {collectionsLoading ? (
                            <div className="flex justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                            </div>
                        ) : (
                            <nav className="space-y-1">
                                {displayedCollections.map((collection) => (
                                    <button
                                        key={collection.id}
                                        onClick={() => fetchCollectionById(collection.id)}
                                        className={`w-full text-left p-3 rounded-lg transition-colors group ${currentCollection?.collection_id === collection.id
                                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center">
                                                    <FolderIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${currentCollection?.collection_id === collection.id
                                                        ? 'text-blue-500'
                                                        : 'text-gray-400 group-hover:text-gray-500'
                                                        }`} />
                                                    <div className="font-medium text-sm truncate">
                                                        {collection.brand}
                                                    </div>
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                                                    {collection.designer}
                                                </div>
                                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 ml-6">
                                                    {collection.season} • {new Date(collection.created_at).toLocaleDateString()}
                                                </div>
                                                {collection.stats && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-6 space-y-1">
                                                        <div className="flex justify-between">
                                                            <span>Looks:</span>
                                                            <span>{collection.stats.total_looks}</span>
                                                        </div>
                                                        {collection.stats.avg_score > 0 && (
                                                            <div className="flex justify-between">
                                                                <span>Avg:</span>
                                                                <span>{collection.stats.avg_score}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex justify-between">
                                                            <span>Progress:</span>
                                                            <span>{collection.stats.completion_rate}%</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {displayedCollections.length === 0 && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 py-4 text-center">
                                        No collections match current controls.
                                    </div>
                                )}
                            </nav>
                        )}

                        {/* Pagination */}
                        {Math.ceil(totalCollections / 20) > 1 && (
                            <div className="flex justify-center items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => setCollectionsPage(prev => Math.max(1, prev - 1))}
                                    disabled={collectionsPage === 1}
                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                </button>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {collectionsPage}/{Math.ceil(totalCollections / 20)}
                                </span>
                                <button
                                    onClick={() => setCollectionsPage(prev => Math.min(Math.ceil(totalCollections / 20), prev + 1))}
                                    disabled={collectionsPage >= Math.ceil(totalCollections / 20)}
                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    if (loading && !currentCollection) {
        return (
            <div className="flex h-screen">
                <Sidebar />
                <div className="flex-1 p-6">
                    <LoadingSkeleton />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen">
                <Sidebar />
                <div className="flex-1 p-6">
                    <div className="p-4 text-center text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <h2 className="text-lg font-semibold mb-2">Error Loading Collections</h2>
                        <p>{error}</p>
                        <button
                            onClick={() => {
                                setError(null);
                                fetchAllCollections();
                                if (collections.length > 0) fetchLatestCollection();
                            }}
                            className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const sortedLooks = getSortedAndFilteredLooks();

    return (
        <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
            <Sidebar />

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Fashion Collections
                            </h1>
                            {currentCollection && (
                                <p className="text-gray-600 dark:text-gray-400 mt-1">
                                    {currentCollection.brand} - {currentCollection.season} • {currentCollection.designer}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Current Collection Stats */}
                    {currentCollection && (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {currentCollection.look_count}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Looks</div>
                                </div>
                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                        {currentCollection.avg_score || 'N/A'}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">Avg Score</div>
                                </div>
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                        {currentCollection.high_rated}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">High Rated (8+)</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                                        {currentCollection.unrated}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">Unrated</div>
                                </div>
                            </div>

                            {currentCollection.review && (
                                <div className="mt-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center justify-between mb-2">
                                        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Collection Review</h2>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setIsCollectionReviewVisible((prev) => !prev)}
                                                className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            >
                                                {isCollectionReviewVisible ? 'Hide' : 'Show'}
                                            </button>
                                            <span className="text-xs px-2 py-1 rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-500">
                                                {detectReviewLanguage(currentCollection.review)}
                                            </span>
                                        </div>
                                    </div>
                                    {isCollectionReviewVisible && (
                                        <p className="text-sm leading-6 text-gray-700 dark:text-gray-200 whitespace-pre-line">
                                            {currentCollection.review}
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Controls */}
                <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex flex-wrap gap-4 items-center">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Sort by:
                            </label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="index">Index</option>
                                <option value="buyer_score">Buyer Score</option>
                                <option value="machine_score">AI Score</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Order:
                            </label>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Filter:
                            </label>
                            <select
                                value={filterBy}
                                onChange={(e) => setFilterBy(e.target.value)}
                                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="all">All Looks</option>
                                <option value="rated">Rated Only</option>
                                <option value="unrated">Unrated Only</option>
                                <option value="high_rated">High Rated (8+)</option>
                            </select>
                        </div>

                        <div className="text-sm text-gray-600 dark:text-gray-400 ml-auto">
                            Showing {sortedLooks.length} of {looks.length} looks
                        </div>
                    </div>
                </div>

                {/* Looks Grid */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {sortedLooks.map((look) => (
                            <LookCard key={look.id} look={look} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Look Detail Modal */}
            {selectedLook && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Look #{selectedLook.index}
                                </h3>
                                <button
                                    onClick={() => setSelectedLook(null)}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="mb-4">
                                <img
                                    src={selectedLook.image_url}
                                    alt={`Look ${selectedLook.index}`}
                                    className="w-full max-w-md mx-auto rounded-lg"
                                    onError={(e) => {
                                        e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
                                    }}
                                />
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Brand:</span>
                                        <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedLook.brand}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Designer:</span>
                                        <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedLook.designer}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Season:</span>
                                        <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedLook.season}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Year:</span>
                                        <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedLook.year}</span>
                                    </div>
                                </div>

                                <div className="border-t dark:border-gray-600 pt-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                            Buyer Score
                                        </span>
                                        <span className={`text-xl font-bold ${selectedLook.buyer_score >= 8 ? 'text-green-600' :
                                            selectedLook.buyer_score >= 6 ? 'text-yellow-600' :
                                                selectedLook.buyer_score > 0 ? 'text-red-600' : 'text-gray-400'
                                            }`}>
                                            {selectedLook.buyer_score || 'Not rated'}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 justify-center">
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                                            <ScoreButton
                                                key={score}
                                                score={score}
                                                currentScore={selectedLook.buyer_score}
                                                onClick={(score) => {
                                                    updateBuyerScore(selectedLook.id, score);
                                                    setSelectedLook(prev => ({ ...prev, buyer_score: score }));
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <div className="mt-3 flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="0"
                                            max="10"
                                            step="0.1"
                                            value={modalManualScore}
                                            onChange={(e) => setModalManualScore(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const parsed = parseFloat(modalManualScore);
                                                    if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
                                                        setModalManualError('Enter 0-10 (decimals allowed).');
                                                        return;
                                                    }
                                                    const normalized = Math.round(parsed * 100) / 100;
                                                    setModalManualError('');
                                                    updateBuyerScore(selectedLook.id, normalized);
                                                    setSelectedLook(prev => ({ ...prev, buyer_score: normalized }));
                                                }
                                            }}
                                            placeholder="e.g. 7.5"
                                            className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const parsed = parseFloat(modalManualScore);
                                                if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
                                                    setModalManualError('Enter 0-10 (decimals allowed).');
                                                    return;
                                                }
                                                const normalized = Math.round(parsed * 100) / 100;
                                                setModalManualError('');
                                                updateBuyerScore(selectedLook.id, normalized);
                                                setSelectedLook(prev => ({ ...prev, buyer_score: normalized }));
                                            }}
                                            className="px-3 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
                                        >
                                            Save
                                        </button>
                                    </div>
                                    {modalManualError && (
                                        <div className="mt-1 text-xs text-red-500">{modalManualError}</div>
                                    )}
                                </div>

                                {selectedLook.machine_score && (
                                    <div className="border-t dark:border-gray-600 pt-4">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                AI Score
                                            </span>
                                            <span className="text-xl font-bold text-green-600">
                                                {selectedLook.machine_score}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div className="border-t dark:border-gray-600 pt-4">
                                    <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        AI Auxiliary Buying Decision
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-6 whitespace-pre-line">
                                        {selectedLook.ai_auxiliary_buying_decision && String(selectedLook.ai_auxiliary_buying_decision).trim()
                                            ? selectedLook.ai_auxiliary_buying_decision
                                            : 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Render Time Display */}
            {showRenderTime && (
                <div className="fixed bottom-6 right-6 bg-gray-800 text-white text-xs px-3 py-1 rounded shadow-lg animate-fade-in z-50">
                    Load Time: {renderTime} seconds
                </div>
            )}
        </div>
    );
};

export default Collections;
